/** Minimal edge shape for flow-editor helpers (definition + instance graphs). */
export interface FlowEdgeLike {
  readonly source?: string;
  readonly target?: string;
  readonly type?: string;
  readonly id?: string;
  readonly data?: unknown;
}

export const isSelfLoopEdge = (edge: FlowEdgeLike | null | undefined): boolean => Boolean(edge?.source && edge.source === edge.target);

export const isLoopbackEdgeType = (type: string | undefined): boolean => type === 'loopback' || type === 'error-loopback';

/** Whether the loop-restart limits inspector applies to this edge. */
export const isLoopRestartConfigurableEdge = (edge: FlowEdgeLike | null | undefined): boolean =>
  Boolean(edge && (isLoopbackEdgeType(edge.type) || isSelfLoopEdge(edge)));

/** Display type for LoopEdgeRestartCard — self-loops are always loopback unless error-loopback. */
export const resolveLoopEdgeDisplayType = (edge: FlowEdgeLike): string => {
  if (isSelfLoopEdge(edge) && edge.type !== 'error-loopback') {
    return 'loopback';
  }
  return edge.type || 'loopback';
};

/** Persist/render coercion: self-loops must be loopback (legacy data may be `default`). */
export const coerceSelfLoopEdgeType = (edge: FlowEdgeLike): string => {
  if (isSelfLoopEdge(edge) && !isLoopbackEdgeType(edge.type)) {
    return 'loopback';
  }
  return edge.type ?? 'default';
};

/** Property-panel header for the selected node or edge. */
export const buildPropertyPanelTitle = (selectedEdge: FlowEdgeLike | null, selectedNodeName?: string | null): string => {
  if (selectedEdge) {
    if (isSelfLoopEdge(selectedEdge)) {
      return 'Properties - Self-loop edge';
    }
    if (isLoopbackEdgeType(selectedEdge.type)) {
      return `Properties - ${selectedEdge.type} edge`;
    }
    return 'Properties - Edge';
  }
  if (selectedNodeName) {
    return `Properties - ${selectedNodeName}`;
  }
  return 'Properties';
};

/**
 * How an edge is drawn. Each variant is also a react-flow `edgeTypes` key, so a canvas edge's `type` must be one of them
 * (see `flowEdgeTypes`).
 */
export type FlowEdgeVariant = 'default' | 'loopback' | 'error-loopback';

/** Self-loops always draw as a restart loop (see `coerceSelfLoopEdgeType`); a missing or unknown type draws as a forward edge. */
export const flowEdgeVariant = (edge: FlowEdgeLike): FlowEdgeVariant => {
  if (edge.type === 'error-loopback') return 'error-loopback';
  if (edge.type === 'loopback' || isSelfLoopEdge(edge)) return 'loopback';
  return 'default';
};

/** Canvas/render memo: set `type` to the edge's variant so react-flow picks its renderer. Edges already typed by variant are returned as is. */
export const coerceEdgeTypeForCanvas = <E extends FlowEdgeLike>(edge: E): E => {
  const variant = flowEdgeVariant(edge);
  return edge.type === variant ? edge : { ...edge, type: variant };
};
